import {
  CompanyType,
  EnterpriseOrgStatusType,
  OrganizationPayload,
} from '@consolidocs-ui/types';
import { enhanceEndpoints } from '../core';

const CompanyApiWithTag = enhanceEndpoints({
  addTagTypes: ['companies', 'enterpriseOrgStatus'],
});

// Company & Department API Services
export const CompanyApi = CompanyApiWithTag.injectEndpoints({
  endpoints: (builder) => ({
    getCompanies: builder.query<CompanyType[], void>({
      query: () => ({
        url: 'organizations',
      }),
      providesTags: ['companies'],
    }),

    // Live infrastructure state for every enterprise organization. Polled by
    // the wizard after an enterprise org is created; callers pick their row
    // out of the list by id.
    getEnterpriseOrgStatus: builder.query<EnterpriseOrgStatusType[], void>({
      query: () => ({
        url: 'enterprise-org-status',
      }),
      providesTags: ['enterpriseOrgStatus'],
    }),

    createCompany: builder.mutation<CompanyType, OrganizationPayload>({
      query: (company) => {
        return {
          url: `organizations`,
          method: 'POST',
          data: company,
        };
      },
      invalidatesTags: ['companies'],
    }),

    updateCompany: builder.mutation<
      CompanyType,
      OrganizationPayload & { id?: string }
    >({
      query: ({ id, ...company }) => {
        return {
          url: `organizations/${id}`,
          method: 'PATCH',
          data: company,
        };
      },
      invalidatesTags: ['companies'],
    }),

    // Starts a fresh CodeBuild run for an enterprise organization whose
    // provisioning failed. The backend answers 409 while a build is running.
    retryProvisioning: builder.mutation<unknown, string>({
      query: (id) => ({
        url: `organizations/${id}/retry-provisioning`,
        method: 'POST',
      }),
      invalidatesTags: ['companies', 'enterpriseOrgStatus'],
    }),

    deleteCompany: builder.mutation<{ success: boolean }, string>({
      query: (id) => ({
        url: `organizations/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['companies'],
    }),
  }),
});

export const {
  useGetCompaniesQuery,
  useGetEnterpriseOrgStatusQuery,
  useCreateCompanyMutation,
  useDeleteCompanyMutation,
  useUpdateCompanyMutation,
  useRetryProvisioningMutation,
} = CompanyApi;
